<template lang="pug">
Transition(name="fade-in")
  #give-feedback-container(v-if="isOpen")
    IconClose.close-button(@click="close")
    .form-container(v-if="page === 'form'")
      img.logo(:src="'/img/logo-new.png'")
      h1.header Course Catalog Feedback
      p.description MUHSD values your feedback! Thank you for taking the time to provide us with your thoughts and recommendations for improving the Course Catalog Application.

      form.feedback-form(novalidate="true", @submit.prevent="onSubmit")
        //- Don't enable email for now
        //- .input.input-text(:class="{ error: emailErrors[0] }")
        //-   input#email(v-model="email", type="email", placeholder="Email Address")
        //-   p.error {{ emailErrors[0] || '' }}

        .input.input-text-area(
          :class="{ empty: feedback.length === 0, 'no-error': feedback.length > 0, error: feedbackErrors[0] }"
        )
          .text-area-container
            textarea#feedback(
              v-model="feedback",
              placeholder="Add your feedback here...",
              rows="12"
            )
            p.char-length {{ feedback.length }}/{{ maxCharacters }}
          p.error {{ feedbackErrors[0] || '' }}
        button.button.submit-button Submit Feedback
        .disclaimer Your email address will only be used to inform you of updates made regarding your feedback. We will not share your email with third-parties and will never send you spam.
        .error-text {{ errorText }}

    .loading-container(v-else-if="page === 'loading'")
      .loading-circles
        span
        span
        span

    .submitted-container(v-else)
      SuccessIcon.success-icon
      p.submitted-text Thank you!
      p.submitted-text Your feedback has been submitted.
      button.button.exit-button(@click="close") Exit
</template>

<script>
import IconClose from '@/components/IconClose';
import SuccessIcon from '@/components/GiveFeedbackSuccessIcon';
import { SendFeedback } from '@/lib/API';

export default {
  name       : 'give-feedback',
  components : {
    IconClose,
    SuccessIcon,
  },

  data() {
    return {
      page           : 'form', // 'form' | 'loading' | 'submitted'
      email          : '',
      emailErrors    : [],
      feedback       : '',
      feedbackErrors : [],
      errorText      : '',
      maxCharacters  : 360,
    };
  },

  computed : {
    isOpen() {
      return this.$store.getters.giveFeedbackOpen;
    },
  },

  watch : {
    feedback : {
      immediate : true,
      handler( feedback ) {
        this.feedbackErrors = [];
        if ( feedback.length > this.maxCharacters ) this.feedbackErrors.push( 'You are exceeding the character limit.' );
      },
    },
  },

  methods : {
    close() {
      // This component doesn't unmount since we have a computed property telling it
      // to be visible or not. Thus, need to reset state after closing
      this.page = 'form';
      this.feedback = '';
      this.feedbackErrors = [];
      this.$store.dispatch( 'closeGiveFeedback' );
    },

    async onSubmit() {
      const isValid = this.validate();
      if ( isValid ) {
        try {
          this.page = 'loading';
          await SendFeedback( { feedback : this.feedback } );
          this.page = 'submitted';
        }
        catch ( err ) {
          // console.error( 'Unable to send feedback:', err );
          this.page = 'form';
          this.errorText = 'There was a problem submitting your feedback. Please try again later.';
        }
      }
    },

    validate() {
      this.emailErrors = [];
      this.feedbackErrors = [];
      // if (!this.email) {
      //   this.emailErrors.push("Please enter a valid email address.");
      // }

      if ( this.feedback.length > this.maxCharacters ) this.feedbackErrors.push( 'You are exceeding the character limit.' );

      if ( this.feedback.length === 0 ) this.feedbackErrors.push( 'Please provide valid feedback.' );

      return this.emailErrors.length === 0 && this.feedbackErrors.length === 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.3s ease;
}

#give-feedback-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background-color: rgba(226, 231, 234, 1);
  padding: 30px;
  overflow: auto;

  .close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    color: $cancel;
  }

  .form-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 800px;
    margin: 0 auto;

    .logo {
      width: 200px;
    }
    .header {
      font-size: 28px;
      margin-top: 20px;
      color: #36548d;
      text-align: center;
    }
    .description {
      margin-top: 10px;
      text-align: center;
      font-weight: 500;
      color: #36548d;
    }

    .feedback-form {
      margin-top: 30px;
      display: flex;
      flex-direction: column;
      align-items: center;

      @include md {
        margin-left: 30px;
        margin-right: 30px;
      }

      .input {
        width: 100%;
      }
    }

    .submit-button {
      padding: 16px 48px;
    }

    .disclaimer {
      margin-top: 20px;
      color: #36548d;
      text-align: center;
      font-size: 14px;
    }

    .error-text {
      margin-top: 30px;
      color: $error;
      font-weight: 600;
      text-align: center;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .submitted-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .success-icon {
      width: 200px;
      margin-bottom: 40px;
    }

    .submitted-text {
      color: #2668d0;
      font-weight: 500;
      font-size: 22px;
    }

    .exit-button {
      margin-top: 40px;
      min-width: 180px;
      background-color: #2668d0;
      padding: 16px;
    }
  }
}

input,
textarea {
  border: none;
  outline: none;
  background: white;
  border-radius: 12px;
  color: inherit;

  &::placeholder {
    color: rgba(93, 155, 255, 0.51);
    font-weight: 600;
  }
}

.input {
  display: inline-block;

  &.error {
    color: $error;
  }

  .error {
    opacity: none;
    height: 30px;
    text-align: right;
    font-size: 14px;
  }
}

.input-text {
  color: #5d9bff;

  input {
    padding: 20px;
    width: 100%;
    font-weight: 600;
  }
}

.input-text-area {
  &.no-error .text-area-container .char-length {
    color: #50ac49;
  }

  &.error .text-area-container .char-length {
    color: $error;
  }

  .text-area-container {
    position: relative;
    background: white;
    border-radius: 12px;
    padding: 20px;
    padding-bottom: 40px;
    color: #36548d;

    textarea {
      width: 100%;
    }

    .char-length {
      position: absolute;
      bottom: 10px;
      right: 10px;
      color: rgba(93, 155, 255, 0.51);
    }
  }
}
</style>
